import { useState, useEffect, useCallback } from "react";
import { apiFetch, ApiError } from "@/lib/api-client";

// ---------------------------------------------------------------------------
// Types — match api-crm deal serializer output
// ---------------------------------------------------------------------------

export interface DealSummary {
  id: string;
  pipelineId: string;
  stageId: string;
  personId: string | null;
  companyId: string | null;
  title: string;
  value: number | null;
  currency: string;
  expectedCloseDate: string | null;
  score: number;
  ownerId: string | null;
  customFields: Record<string, unknown>;
  lastHumanActivityAt: string | null;
  momentumDirection: "accelerating" | "stable" | "decelerating" | null;
  momentumScore: number | null;
  winProbability: number | null;
  createdAt: string;
}

export interface Deal extends DealSummary {
  closedAt: string | null;
  lostReason: string | null;
  winReason: string | null;
  customFields: Record<string, unknown>;
  lastHumanActivityAt: string | null;
  stageEnteredAt: string;
  updatedAt: string;
}

export interface PipelineStage {
  id: string;
  name: string;
  displayOrder: number;
  color?: string;
  isWinStage?: boolean;
  isLossStage?: boolean;
}

export interface Pipeline {
  id: string;
  name: string;
  isDefault?: boolean;
  stages: PipelineStage[];
}

export interface CreatePipelineInput {
  name: string;
  isDefault: boolean;
  stages: PipelineStageInput[];
}

export interface PipelineStageInput {
  id: string;
  name: string;
  color: string;
  isWinStage?: boolean;
  isLossStage?: boolean;
  maxDays?: number | null;
  requiredFields?: string[];
  autoTasks?: Array<{
    title: string;
    type: string;
    priority: string;
    dueOffsetDays: number;
    assignTo: "deal_owner" | "specific_user";
    assignUserId?: string;
  }>;
}

// ---------------------------------------------------------------------------
// Hook: useDealList — fetch deals with optional pipeline/stage/owner filter
// ---------------------------------------------------------------------------

export function useDealList(pipelineId?: string, ownerId?: string) {
  const [deals, setDeals] = useState<DealSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchDeals = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    const params = new URLSearchParams({ limit: "200" });
    if (pipelineId) params.set("pipelineId", pipelineId);
    if (ownerId) params.set("ownerId", ownerId);

    try {
      const res = await apiFetch<{ data: { items: DealSummary[] } }>(
        `/crm/deals?${params.toString()}`,
      );
      setDeals(res.data.items);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to load deals");
    } finally {
      setIsLoading(false);
    }
  }, [ownerId, pipelineId]);

  useEffect(() => { fetchDeals(); }, [fetchDeals]);

  return { deals, isLoading, error, refetch: fetchDeals };
}

// ---------------------------------------------------------------------------
// Hook: usePipelines — fetch pipeline configurations
// ---------------------------------------------------------------------------

export function usePipelines() {
  const [pipelines, setPipelines] = useState<Pipeline[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPipelines = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    apiFetch<{ data: Pipeline[] }>("/crm/pipelines")
      .then((res) => setPipelines(res.data))
      .catch((err) => setError(err instanceof ApiError ? err.message : "Failed to load pipelines"))
      .finally(() => setIsLoading(false));
  }, []);

  useEffect(() => {
    void fetchPipelines();
  }, [fetchPipelines]);

  return { pipelines, isLoading, error, refetch: fetchPipelines };
}

export async function createPipeline(input: CreatePipelineInput): Promise<Pipeline> {
  const res = await apiFetch<{ data: Pipeline }>("/crm/pipelines", {
    method: "POST",
    body: JSON.stringify(input),
  });
  return res.data;
}

export async function updatePipeline(
  id: string,
  input: { name?: string; isDefault?: boolean },
): Promise<void> {
  await apiFetch(`/crm/pipelines/${id}`, {
    method: "PATCH",
    body: JSON.stringify(input),
  });
}

export async function addPipelineStage(
  pipelineId: string,
  input: PipelineStageInput,
): Promise<PipelineStage> {
  const res = await apiFetch<{ data: PipelineStage }>(`/crm/pipelines/${pipelineId}/stages`, {
    method: "POST",
    body: JSON.stringify(input),
  });
  return res.data;
}

export async function removePipelineStage(pipelineId: string, stageId: string): Promise<void> {
  await apiFetch(`/crm/pipelines/${pipelineId}/stages/${stageId}`, { method: "DELETE" });
}

export async function reorderPipelineStages(pipelineId: string, stageIds: string[]): Promise<void> {
  await apiFetch(`/crm/pipelines/${pipelineId}/stages/reorder`, {
    method: "PUT",
    body: JSON.stringify({ stageIds }),
  });
}

// ---------------------------------------------------------------------------
// Hook: useDeal — single deal detail
// ---------------------------------------------------------------------------

export function useDeal(id: string | null) {
  const [deal, setDeal] = useState<Deal | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchDeal = useCallback(async () => {
    if (!id) {
      setDeal(null);
      return null;
    }

    setIsLoading(true);
    setError(null);

    try {
      const res = await apiFetch<{ data: Deal }>(`/crm/deals/${id}`);
      setDeal(res.data);
      return res.data;
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to load deal");
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => { void fetchDeal(); }, [fetchDeal]);

  return { deal, isLoading, error, refetch: fetchDeal };
}

// ---------------------------------------------------------------------------
// Action: create deal
// ---------------------------------------------------------------------------

export interface CreateDealInput {
  title: string;
  value: number | null;
  stageId: string;
  pipelineId: string;
  personId: string | null;
  expectedCloseDate: string | null;
  notes: string;
}

interface CreateDealPayload {
  title: string;
  stageId: string;
  pipelineId: string;
  value?: number;
  personId?: string;
  expectedCloseDate?: string;
  customFields?: Record<string, unknown>;
}

function normalizeExpectedCloseDate(value: string | null): string | undefined {
  const trimmed = value?.trim();
  if (!trimmed) return undefined;

  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    return new Date(`${trimmed}T00:00:00.000Z`).toISOString();
  }

  const parsed = new Date(trimmed);
  return Number.isNaN(parsed.getTime()) ? undefined : parsed.toISOString();
}

function buildCreateDealPayload(input: CreateDealInput): CreateDealPayload {
  const notes = input.notes.trim();
  const expectedCloseDate = normalizeExpectedCloseDate(input.expectedCloseDate);
  return {
    title: input.title,
    stageId: input.stageId,
    pipelineId: input.pipelineId,
    ...(input.value !== null ? { value: input.value } : {}),
    ...(input.personId ? { personId: input.personId } : {}),
    ...(expectedCloseDate ? { expectedCloseDate } : {}),
    ...(notes ? { customFields: { notes } } : {}),
  };
}

export async function createDeal(input: CreateDealInput): Promise<DealSummary> {
  const res = await apiFetch<{ data: DealSummary }>("/crm/deals", {
    method: "POST",
    body: JSON.stringify(buildCreateDealPayload(input)),
  });
  return res.data;
}

// ---------------------------------------------------------------------------
// Actions: update/delete deal
// ---------------------------------------------------------------------------

export interface UpdateDealInput {
  title?: string;
  value?: number | null;
  currency?: string;
  expectedCloseDate?: string | null;
  ownerId?: string | null;
  customFields?: Record<string, unknown>;
  lostReason?: string;
  winReason?: string;
}

interface UpdateDealPayload {
  title?: string;
  value?: number;
  currency?: string;
  expectedCloseDate?: string;
  ownerId?: string | null;
  customFields?: Record<string, unknown>;
  lostReason?: string;
  winReason?: string;
}

function buildUpdateDealPayload(input: UpdateDealInput): UpdateDealPayload {
  const title = trimmedValue(input.title);
  const currency = trimmedValue(input.currency);
  const ownerId = normalizeUpdateOwnerId(input.ownerId);
  const expectedCloseDate = normalizeExpectedCloseDate(input.expectedCloseDate ?? null);
  const lostReason = trimmedValue(input.lostReason);
  const winReason = trimmedValue(input.winReason);

  return {
    ...(title ? { title } : {}),
    ...(typeof input.value === "number" ? { value: input.value } : {}),
    ...(currency ? { currency } : {}),
    ...(expectedCloseDate ? { expectedCloseDate } : {}),
    ...(ownerId !== undefined ? { ownerId } : {}),
    ...(input.customFields ? { customFields: input.customFields } : {}),
    ...(lostReason ? { lostReason } : {}),
    ...(winReason ? { winReason } : {}),
  };
}

function normalizeUpdateOwnerId(value: string | null | undefined): string | null | undefined {
  if (value === undefined) return undefined;
  if (value === null) return null;
  return trimmedValue(value);
}

function trimmedValue(value: string | undefined): string | undefined {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
}

export async function updateDeal(id: string, input: UpdateDealInput): Promise<Deal> {
  const res = await apiFetch<{ data: Deal }>(`/crm/deals/${id}`, {
    method: "PATCH",
    body: JSON.stringify(buildUpdateDealPayload(input)),
  });
  return res.data;
}

export async function deleteDeal(id: string): Promise<void> {
  await apiFetch(`/crm/deals/${id}`, { method: "DELETE" });
}

// ---------------------------------------------------------------------------
// Action: change deal stage
// ---------------------------------------------------------------------------

export async function changeDealStage(
  dealId: string,
  toStage: string,
  reason?: string,
): Promise<void> {
  await apiFetch(`/crm/deals/${dealId}/stage`, {
    method: "POST",
    body: JSON.stringify({ toStage, ...(reason ? { reason } : {}) }),
  });
}
